import React, { useState, useEffect, useRef } from 'react';
import { chatService } from '../../services/chatService';
import { contactService } from '../../services/contactService';
import sseService from '../../services/sseService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ArrowLeft, Send, Check, CheckCheck, Clock, Users, Plus, XCircle } from 'lucide-react';
import AddToGroupModal from '../contact/AddToGroupModal';

const ChatInterface = ({ contactId, onBack }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [contact, setContact] = useState(null);
  const [chatMode, setChatMode] = useState('BOT');
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [contactGroups, setContactGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);

  useEffect(() => {
    loadContact();
    loadMessages();
    markConversationRead(contactId);
    
    // Poll for new messages every 3 seconds
    const interval = setInterval(() => {
      loadMessages(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [contactId]);

  useEffect(() => {
    if (!user?.customerId) return;

    sseService.connect(user.customerId);
    const onChatMessage = (event) => {
      if (!event || String(event.contactId) !== String(contactId)) {
        return;
      }

      setMessages((prev) => {
        const exists = prev.some((msg) =>
          (event.messageId && msg.messageId === event.messageId) ||
          (event.metaMessageId && msg.metaMessageId === event.metaMessageId)
        );
        if (exists) {
          return prev;
        }
        return [...prev, event];
      });
    };

    sseService.addEventListener('CHAT_MESSAGE', onChatMessage);
    return () => {
      sseService.removeEventListener('CHAT_MESSAGE', onChatMessage);
    };
  }, [user?.customerId, contactId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadContact = async () => {
    try {
      const data = await contactService.getContactById(contactId);
      setContact(data);
      setChatMode(data?.chatMode || 'BOT');
      // Load groups for this contact
      if (data?.phoneNumber) {
        loadContactGroups(data.phoneNumber);
      }
    } catch (error) {
      console.error('Error loading contact:', error);
      toast.error('Failed to load contact');
    }
  };

  const markConversationRead = async (targetContactId) => {
    try {
      const result = await chatService.markConversationRead(targetContactId);
      if ((result?.updatedCount || 0) > 0) {
        window.dispatchEvent(new CustomEvent('chat:conversation-read', { detail: { contactId: targetContactId } }));
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  const loadContactGroups = async (phoneNumber) => {
    setLoadingGroups(true);
    try {
      const response = await contactService.searchContactGroupsByPhoneNumber(phoneNumber, 0, 100);
      setContactGroups(response.content || []);
    } catch (error) {
      console.error('Error loading contact groups:', error);
      // Don't show error toast as this is not critical
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadMessages = async (silent = false) => {
    try {
      const data = await chatService.getConversationHistory(contactId, 0, 50);
      // Reverse to show oldest first, newest last
      const reversedMessages = [...(data.content || [])].reverse();
      setMessages(reversedMessages);
      setHasMore(data.totalElements > data.content.length);
      if (!silent) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      if (!silent) {
        toast.error('Failed to load messages');
        setLoading(false);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || sending) return;

    const messageContent = messageInput.trim();
    setMessageInput('');
    setSending(true);

    try {
      const newMessage = await chatService.sendMessage(contactId, messageContent);
      // Add the new message to the list
      setMessages(prev => [...prev, newMessage]);
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setMessageInput(messageContent); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const handleToggleChatMode = async (nextMode) => {
    const previous = chatMode;
    setChatMode(nextMode);
    try {
      await chatService.updateChatMode(contactId, nextMode);
      setContact((prev) => prev ? { ...prev, chatMode: nextMode } : prev);
      toast.success(`Switched to ${nextMode} mode`);
    } catch (error) {
      console.error('Failed to update chat mode:', error);
      setChatMode(previous);
      toast.error('Failed to update chat mode');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateHeader = (timestamp, prevTimestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const prevDate = prevTimestamp ? new Date(prevTimestamp) : null;
    
    if (prevDate && date.toDateString() === prevDate.toDateString()) {
      return null;
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
  };

  const getStatusIcon = (direction, status) => {
    if (direction === 'inbound') return null;

    const normalizedStatus = String(status || '').trim().toLowerCase();

    if (normalizedStatus === 'read' || normalizedStatus === 'delivered') {
      return <CheckCheck className="w-4 h-4 text-primary-600" />;
    }
    if (normalizedStatus === 'sent') {
      return <Check className="w-4 h-4 text-gray-400" />;
    }
    if (normalizedStatus === 'failed') {
      return <XCircle className="w-4 h-4 text-error-500" />;
    }
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">
              {contact?.contactName || 'Contact'}
            </h2>
            <p className="text-sm text-gray-500">{contact?.phoneNumber}</p>
            <div className="mt-2 inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => handleToggleChatMode('BOT')}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  chatMode === 'BOT'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                BOT
              </button>
              <button
                type="button"
                onClick={() => handleToggleChatMode('AGENT')}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  chatMode === 'AGENT'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                AGENT
              </button>
            </div>
            
            {/* Contact Groups */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {loadingGroups ? (
                <span className="text-xs text-gray-400">Loading groups...</span>
              ) : contactGroups.length > 0 ? (
                <>
                  {contactGroups.map((group) => (
                    <span
                      key={group.groupId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full"
                    >
                      <Users className="w-3 h-3" />
                      {group.groupName}
                    </span>
                  ))}
                  <button
                    onClick={() => setShowAddToGroupModal(true)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-full transition-colors"
                    title="Add to group"
                  >
                    <Plus className="w-3 h-3" />
                    Add to Group
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowAddToGroupModal(true)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-full transition-colors"
                  title="Add to group"
                >
                  <Plus className="w-3 h-3" />
                  Add to Group
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
      >
        {messages.map((message, index) => {
          const isOutbound = message.direction === 'outbound';
          const prevMessage = index > 0 ? messages[index - 1] : null;
          const dateHeader = formatDateHeader(message.sentAt || message.receivedAt, prevMessage?.sentAt || prevMessage?.receivedAt);

          return (
            <React.Fragment key={message.messageId}>
              {dateHeader && (
                <div className="flex justify-center my-4">
                  <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                    {dateHeader}
                  </span>
                </div>
              )}
              <div
                className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    isOutbound
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.content}
                  </p>
                  <div className={`flex items-center justify-end gap-1 mt-1 ${
                    isOutbound ? 'text-primary-100' : 'text-gray-500'
                  }`}>
                    <span className="text-xs">
                      {formatMessageTime(message.sentAt || message.receivedAt)}
                    </span>
                    {getStatusIcon(message.direction, message.messageStatus)}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-white border-t border-gray-200 px-4 py-3">
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!messageInput.trim() || sending}
            className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>

      {/* Add to Group Modal */}
      {contact && (
        <AddToGroupModal
          isOpen={showAddToGroupModal}
          onClose={() => setShowAddToGroupModal(false)}
          contact={contact}
          onSuccess={() => {
            if (contact?.phoneNumber) {
              loadContactGroups(contact.phoneNumber);
            }
          }}
        />
      )}
    </div>
  );
};

export default ChatInterface;

